<template>
  <v-container fluid>
    <v-row v-if="!isLoading">
      <v-col>
        <v-card width="100%" class="pa-4">
          <div class="d-flex align-center mb-3">
            <v-btn small outlined color="primary" @click="v3EditorOpen = true">
              <v-icon small left>mdi-file-tree</v-icon>V3 Navigation Editor
            </v-btn>
            <span class="v3-hint ml-4 mb-0">
              Drag-and-drop editor for the new navigation — reorder the rail,
              move pages between areas, create groups.
            </span>
          </div>
          <div class="v3-hint">
            <b>v3.*</b> columns configure the new navigation (V2 layout):
            <b>rail</b> = top-level bar position, <b>parent</b> = which area's
            "On this page" list the page appears in, <b>order</b> = position
            within that list, <b>tocText</b> = label override in the list,
            <b>section</b> = labelled sub-group header, <b>icon</b> = Material
            Symbols name. Enter <b>none</b> to remove a property (e.g. pull a
            page out of the rail/ToC); clear an override to restore the default
            shown above it. Changes apply on next sign-in / refresh.
          </div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="py-2"><h2>Section</h2></th>
                  <th class="py-2">
                    <h2>
                      Page
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on">
                            mdi-filter-menu
                          </v-icon>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="(f, fi) in routeFilter.list"
                            :key="'f' + fi"
                            @click="filterRoutes(f)"
                          >
                            <v-list-item-title
                              ><b v-if="routeFilter.value === f">{{ f }}</b
                              ><span v-else>{{ f }}</span></v-list-item-title
                            >
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </h2>
                  </th>
                  <th class="py-2" :colspan="metaKeys.length">
                    <h2>Meta Properties</h2>
                  </th>
                </tr>
                <tr>
                  <th colspan="2"></th>
                  <th v-for="mk in metaKeys" :key="mk">
                    <v-chip small class="mb-2">{{ mk }}</v-chip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, ri) in routeFilter.routes" :key="'r' + ri">
                  <td>{{ r.section }}</td>
                  <td>
                    <v-icon v-if="r.icon" :class="r.icon">{{
                      r.icon.split(" ")[0]
                    }}</v-icon>
                    {{ r.label }}
                    <!-- Titles collide (two pages can both be "Home") —
                         the route name is the real identity. -->
                    <div class="text-caption grey--text">
                      {{ r.name }}<span v-if="r.isLandingPage"> · landing page</span>
                    </div>
                  </td>
                  <td v-for="mk in metaKeys" :key="mk" class="meta-cell">
                    <div class="meta-default">{{ r.defaults[mk] }}</div>
                    <!-- Landing pages: title/icon are managed in Landing
                         Config — an override here would silently fight it. -->
                    <div
                      v-if="r.isLandingPage && (mk === 'text' || mk === 'icon')"
                      class="meta-override text-caption grey--text"
                    >
                      via Landing Config
                    </div>
                    <v-edit-dialog
                      v-else
                      large
                      persistent
                      @save="saveCell(r, mk)"
                    >
                      <div
                        class="meta-override"
                        :class="{ 'has-value': r.overrides[mk] !== undefined && r.overrides[mk] !== '' }"
                      >
                        {{ r.overrides[mk] !== undefined && r.overrides[mk] !== '' ? r.overrides[mk] : '—' }}
                        <v-icon x-small class="meta-edit-icon ml-1">mdi-pencil</v-icon>
                      </div>
                      <template v-slot:input>
                        <v-select
                          v-if="mk === 'atlas_context_workflow'"
                          v-model="r.overrides[mk]"
                          :items="workflowOptions"
                          :label="mk"
                          dense
                          clearable
                          class="mt-2"
                          style="min-width: 280px"
                          hint="Workflow auto-started when a new Atlas conversation begins on this page"
                          persistent-hint
                        ></v-select>
                        <v-select
                          v-else-if="mk === 'v3.parent'"
                          v-model="r.overrides[mk]"
                          :items="parentOptions"
                          :label="mk"
                          dense
                          clearable
                          class="mt-2"
                        ></v-select>
                        <v-text-field
                          v-else
                          v-model="r.overrides[mk]"
                          :type="mk === 'v3.rail' || mk === 'v3.order' ? 'number' : 'text'"
                          :label="mk"
                          single-line
                          dense
                          clearable
                          class="mt-2"
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="v3EditorOpen" max-width="1100" scrollable>
      <V3NavEditor
        v-if="v3EditorOpen"
        :saved-overrides="savedOverrides"
        @close="v3EditorOpen = false"
        @saved="onV3Saved"
        @sessionExpired="$emit('sessionExpired', $event)"
      />
    </v-dialog>
    <Loading :isVisible="isLoading" />
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
  </v-container>
</template>

<script>
import axios from "axios";
import ResponseHandler from "@/components/ResponseHandler";
import V3NavEditor from "@/components/admin/V3NavEditor.vue";

export default {
  name: "RouteMetaAdmin",
  components: {
    ResponseHandler,
    V3NavEditor,
  },
  data: function () {
    return {
      isLoading: true,
      response: null,
      clientRoutes: [],
      savedOverrides: [],
      // Published guided workflows, for the atlas_context_workflow picker.
      workflowOptions: [],
      v3EditorOpen: false,
      metaKeys: [
        "text",
        "icon",
        "header_title",
        "atlas_context_workflow",
        "v3.rail",
        "v3.parent",
        "v3.order",
        "v3.tocText",
        "v3.section",
        "v3.icon",
      ],
      routeFilter: {
        list: ["All", "Client Visible", "RoleMapper Admin"],
        value: "Client Visible",
        routes: [],
      },
    };
  },
  created() {
    this.init();
    this.loadWorkflows();
  },
  computed: {
    // Valid v3.parent targets: the synthetic groups plus any route that sits
    // on the rail (an area key is a group key or a rail route's name).
    // 'none' removes the property (pulls the page out of every ToC).
    parentOptions() {
      const groups = (this.$router.v3Groups || []).map((g) => g.key);
      const rails = this.$router
        .getRoutes()
        .filter((r) => (r.meta?.Default?.v3 || r.meta?.v3)?.rail != null)
        .map((r) => r.name);
      return ["none", ...groups, ...Array.from(new Set(rails))];
    },
  },
  methods: {
    loadWorkflows() {
      // Published workflows only; a client-specific row shadows the
      // global template with the same key (mirrors the engine's
      // resolution), so each key appears once.
      axios
        .get("agent/workflows/admin/list")
        .then((resp) => {
          if (resp.data.Status !== "OK") return;
          const byKey = new Map();
          (resp.data.Data || [])
            .filter((w) => w.status === "published")
            .forEach((w) => {
              const existing = byKey.get(w.workflow_key);
              if (!existing || (existing.client_id === null && w.client_id !== null)) {
                byKey.set(w.workflow_key, w);
              }
            });
          // Users know workflows by NAME — the key is the internal wire
          // identity (auto-minted, hidden in the workflow admin too).
          this.workflowOptions = Array.from(byKey.values()).map((w) => ({
            text: w.name,
            value: w.workflow_key,
          }));
        })
        .catch((err) => {
          // Non-fatal — the attribute just falls back to free text display.
          console.log(err);
        });
    },
    // The tree editor saved a new placement set: adopt it, re-apply the
    // overrides onto the live router (so this session's nav/grid reflect the
    // change immediately where reactivity allows) and rebuild the grid rows.
    onV3Saved(updatedOverrides) {
      this.savedOverrides = updatedOverrides;
      this.$router.reapplyRouteMetaOverrides(updatedOverrides);
      this.buildRoutes();
      this.v3EditorOpen = false;
    },
    init() {
      this.isLoading = true;
      let possibleError = false;
      axios
        .get("admin/routeMetaOverrideLoad")
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            this.savedOverrides = resp.data.Data || [];
            this.buildRoutes();
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    },
    buildRoutes() {
      const clientSettings = this.$loginState.user.settings;
      this.clientRoutes = this.$router
        .getRoutes()
        .filter(
          (r) =>
            r.meta?.newUi &&
            !r.meta?.disabled &&
            // Landing pages ARE listed (their atlas_context_workflow /
            // header_title live here like any page); their text/icon
            // stay read-only — Landing Config owns those. Excluding
            // them entirely made pages like the client homepage
            // impossible to give a default workflow, while a static
            // route with the same TITLE ("Home") silently took the
            // override instead.
            !r.meta.internal &&
            (r.meta?.module
              ? clientSettings.find(
                  (x) =>
                    x.setting === r.meta.module_setting &&
                    x.value === "true"
                )
              : true)
        )
        .map((r, ri) => {
          const defaults = {};
          const overrides = {};
          this.metaKeys.forEach((mk) => {
            // Dotted v3.* keys read from the nested default v3 config.
            const val = mk.startsWith("v3.")
              ? r.meta?.Default?.v3?.[mk.slice(3)]
              : r.meta?.Default?.[mk];
            defaults[mk] =
              val === undefined || val === null ? "" : String(val);
            const saved = this.savedOverrides.find(
              (o) => o.route_name === r.name && o.meta_key === mk
            );
            overrides[mk] = saved ? saved.meta_value : "";
          });
          return {
            label: r.meta?.text || r.name,
            icon: r.meta?.icon,
            section: r.meta?.section || "",
            name: r.name,
            isLandingPage: !!r.meta?.landingPageId,
            defaults,
            overrides,
            sectionOrder: this.$router.menuSections.findIndex(
              (s) => s.name === r.meta?.section
            ),
            order: ri,
            reserved: r.meta?.reserved,
          };
        })
        .sort((a, b) =>
          a.sectionOrder > b.sectionOrder
            ? 1
            : a.sectionOrder === b.sectionOrder
            ? a.order > b.order
              ? 1
              : -1
            : -1
        );
      this.filterRoutes(this.routeFilter.value);
    },
    filterRoutes(value) {
      this.routeFilter.value = value;
      switch (value) {
        case "All":
          this.routeFilter.routes = [...this.clientRoutes];
          break;
        case "Client Visible":
          this.routeFilter.routes = this.clientRoutes.filter(
            (x) => !x.reserved
          );
          break;
        case "RoleMapper Admin":
          this.routeFilter.routes = this.clientRoutes.filter(
            (x) => x.reserved
          );
          break;
      }
    },
    saveCell(route, metaKey) {
      const val = route.overrides[metaKey];
      if (
        (metaKey === "v3.rail" || metaKey === "v3.order") &&
        val &&
        val !== "none" &&
        isNaN(Number(val))
      ) {
        alert(`${metaKey} must be a number (or 'none' to remove it)`);
        return;
      }
      const hasSaved = this.savedOverrides.some(
        (o) => o.route_name === route.name && o.meta_key === metaKey
      );

      if (val === undefined || val === null || val === "") {
        if (!hasSaved) return;
        let possibleError = false;
        axios
          .post("admin/routeMetaOverrideDelete", {
            routeName: route.name,
            metaKey: metaKey,
          })
          .then((resp) => {
            possibleError = true;
            this.response = resp.data;
            if (resp.data.Status === "OK") {
              this.savedOverrides = this.savedOverrides.filter(
                (o) => !(o.route_name === route.name && o.meta_key === metaKey)
              );
              route.overrides[metaKey] = "";
            }
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      } else {
        let possibleError = false;
        axios
          .post("admin/routeMetaOverrideSave", {
            overrides: [
              {
                route_name: route.name,
                meta_key: metaKey,
                meta_value: val,
              },
            ],
          })
          .then((resp) => {
            possibleError = true;
            this.response = resp.data;
            if (resp.data.Status === "OK") {
              const existing = this.savedOverrides.find(
                (o) => o.route_name === route.name && o.meta_key === metaKey
              );
              if (existing) {
                existing.meta_value = val;
              } else {
                this.savedOverrides.push({
                  route_name: route.name,
                  meta_key: metaKey,
                  meta_value: val,
                });
              }
            }
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/vars";

.meta-cell {
  min-width: 90px;
}

.v3-hint {
  font-size: 12px;
  color: #777;
  line-height: 1.5;
  margin-bottom: 12px;
  max-width: 900px;
}

.meta-default {
  font-size: 12px;
  color: #999;
  line-height: 1.2;
}

.meta-override {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: #999;
  font-style: italic;

  &.has-value {
    color: #1976d2;
    font-weight: 500;
    font-style: normal;
  }

  .meta-edit-icon {
    opacity: 0.3;
  }

  &:hover .meta-edit-icon {
    opacity: 1;
  }
}
</style>
